import { LegalNameStep } from '../components/ApplicationEdit/Sections/1PersonalInfo/1LegalNameStep';
import { ContactStep } from '../components/ApplicationEdit/Sections/1PersonalInfo/2ContactStep';
import { MoveInDateStep } from '../components/ApplicationEdit/Sections/1PersonalInfo/3MoveInDateStep';
import { OccupantsStep, OccupantsStepTip } from '../components/ApplicationEdit/Sections/2HouseholdInfo/1OccupantsStep';
import { SmokingStep } from '../components/ApplicationEdit/Sections/2HouseholdInfo/2SmokingStep';
import {
  CurrentResidenceStep,
  CurrentResidenceStepTip,
} from '../components/ApplicationEdit/Sections/4ResidenceHistory/1CurrentResidenceStep';
import { GuarantorCurrentResidenceStep } from '../components/ApplicationEdit/Sections/4ResidenceHistory/1GuarantorCurrentResidenceStep';
import {
  GuarantorCurrentIncomeStepTip,
  GuarantorCurrentResidenceStepTip,
  GuarantorPastJobsStepTip,
  GuarantorPaymentAndReportsStepTip,
} from '../components/Guarantor/GuarantorStepTips';
import type { WizardFieldType, WizardSectionType, WizardStepType } from '../models';
import { ApplicationRole, HomeType, IncomeType } from '@zg-rentals/ts-capps-client';
import type { RentalListing } from '../models/rental-application';
import {
  CurrentIncomeRecommendation,
  CurrentIncomeStep,
  CurrentIncomeStepTip,
} from '../components/ApplicationEdit/Sections/3IncomeHistory/1CurrentIncomeStep';
import { PetsStep, PetsStepTip } from '../components/ApplicationEdit/Sections/2HouseholdInfo/3PetsStep';
import { PastResidencesStep } from '../components/ApplicationEdit/Sections/4ResidenceHistory/2PastResidencesStep';
import { PastJobsStep, PastJobsStepTip } from '../components/ApplicationEdit/Sections/3IncomeHistory/2PastJobsStep';
import {
  RequiredDocumentsRecommendation,
  RequiredDocumentsStep,
  RequiredDocumentsStepTip,
} from '../components/ApplicationEdit/Sections/3IncomeHistory/RequiredDocumentsStep';
import {
  PaymentAndReportsStep,
  PaymentAndReportsStepTip,
  ReAuthRecommendation,
} from '../components/ApplicationEdit/Sections/6Reports/1PaymentAndReportsStep';
import { getIsIdvStepComplete, getIsPaymentStepComplete } from '../utils/user-statuses';
import { IDVerificationStep } from '../components/ApplicationEdit/Sections/5IDVerification/1IDVerificationStep';
import {
  SupplementalStep,
  SupplementalStepTip,
} from '../components/ApplicationEdit/Sections/7Supplemental/1SupplementalStep';
import {
  getIsAdditionalOccupantDateOfBirthValid,
  getIsIncomeDocumentsValid,
  getIsMoveInDateValid,
  getIsPaymentAndReportsStepValid,
  getIsPreferredLeaseLengthsValid,
} from './validation';
import type { FeatureFlags } from '../contexts/ApplicationContext';
import type { ValueOf } from 'type-fest';

export enum STEP_IDS {
  LEGAL_NAME = 'legal-name',
  CONTACT = 'contact',
  OCCUPANTS = 'occupants',
  SMOKING = 'smoking',
  PETS = 'pets',
  MOVE_IN_DATE = 'move-in-date',
  ID_VERIFICATION = 'id-verification',
  CURRENT_INCOME = 'current-income',
  REQUIRED_DOCUMENTS = 'required-documents',
  PAST_JOBS = 'past-jobs',
  CURRENT_RESIDENCE = 'current-residence',
  PAST_RESIDENCES = 'past-residences',
  PAYMENT_AND_REPORTS = 'reports',
  SUPPLEMENTAL = 'supplemental',
}

export const STEP_COMPONENTS = {
  [STEP_IDS.LEGAL_NAME]: LegalNameStep,
  [STEP_IDS.CONTACT]: ContactStep,
  [STEP_IDS.MOVE_IN_DATE]: MoveInDateStep,
  [STEP_IDS.OCCUPANTS]: OccupantsStep,
  [STEP_IDS.SMOKING]: SmokingStep,
  [STEP_IDS.PETS]: PetsStep,
  [STEP_IDS.ID_VERIFICATION]: IDVerificationStep,
  [STEP_IDS.CURRENT_INCOME]: CurrentIncomeStep,
  [STEP_IDS.REQUIRED_DOCUMENTS]: RequiredDocumentsStep,
  [STEP_IDS.PAST_JOBS]: PastJobsStep,
  [STEP_IDS.CURRENT_RESIDENCE]: CurrentResidenceStep,
  [STEP_IDS.PAST_RESIDENCES]: PastResidencesStep,
  [STEP_IDS.PAYMENT_AND_REPORTS]: PaymentAndReportsStep,
  [STEP_IDS.SUPPLEMENTAL]: SupplementalStep,
};

export const GUARANTOR_STEP_COMPONENTS = {
  ...STEP_COMPONENTS,
  [STEP_IDS.CURRENT_RESIDENCE]: GuarantorCurrentResidenceStep,
};

export const STEP_TIPS = {
  [STEP_IDS.OCCUPANTS]: OccupantsStepTip,
  [STEP_IDS.CURRENT_RESIDENCE]: CurrentResidenceStepTip,
  [STEP_IDS.CURRENT_INCOME]: CurrentIncomeStepTip,
  [STEP_IDS.REQUIRED_DOCUMENTS]: RequiredDocumentsStepTip,
  [STEP_IDS.PETS]: PetsStepTip,
  [STEP_IDS.PAST_JOBS]: PastJobsStepTip,
  [STEP_IDS.PAYMENT_AND_REPORTS]: PaymentAndReportsStepTip,
  [STEP_IDS.SUPPLEMENTAL]: SupplementalStepTip,
};

export const GUARANTOR_STEP_TIPS = {
  ...STEP_TIPS,
  [STEP_IDS.CURRENT_RESIDENCE]: GuarantorCurrentResidenceStepTip,
  [STEP_IDS.CURRENT_INCOME]: GuarantorCurrentIncomeStepTip,
  [STEP_IDS.PAST_JOBS]: GuarantorPastJobsStepTip,
  [STEP_IDS.PAYMENT_AND_REPORTS]: GuarantorPaymentAndReportsStepTip,
};

export const STEP_RECOMMENDATIONS = {
  [STEP_IDS.CURRENT_INCOME]: CurrentIncomeRecommendation,
  [STEP_IDS.REQUIRED_DOCUMENTS]: RequiredDocumentsRecommendation,
  [STEP_IDS.PAYMENT_AND_REPORTS]: ReAuthRecommendation,
};

export const SECTION_IDS = {
  PERSONAL_INFO: 'personal-info',
  HOUSEHOLD_INFO: 'household-info',
  ID_VERIFICATION: 'id-verification',
  INCOME_HISTORY: 'income-history',
  RESIDENCE_HISTORY: 'residence-history',
  PAYMENT_AND_REPORTS: 'payment-and-reports',
  SUPPLEMENTAL: 'supplemental',
  REVIEW: 'review',
} as const;

/**An union for all the values in @see SECTION_IDS*/
export type SectionId = ValueOf<typeof SECTION_IDS>;

export enum FIELD_IDS {
  FIRST_NAME = 'firstName',
  MIDDLE_NAME = 'middleName',
  LAST_NAME = 'lastName',
  PHONE = 'phone',
  EMAIL = 'email',
  MOBILITY_IMPAIRED = 'customAnswers[0]',
  OCCUPANTS = 'occupants',
  CO_APPLICANTS = 'coapplicants',
  IS_SMOKER = 'isSmoker',
  SMALL_DOGS = 'pets.smallDogs',
  MEDIUM_DOGS = 'pets.mediumDogs',
  LARGE_DOGS = 'pets.largeDogs',
  CATS = 'pets.cats',
  PETS_OTHER = 'pets.other',
  PETS_DESCRIPTION = 'pets.description',
  CURRENT_INCOMES_TYPE = 'currentIncomes[].type',
  CURRENT_INCOMES_AMOUNT = 'currentIncomes[].amount',
  CURRENT_INCOMES_TITLE = 'currentIncomes[].title',
  CURRENT_INCOMES_ORGANIZATION = 'currentIncomes[].employer.organization',
  CURRENT_INCOMES_START_DATE = 'currentIncomes[].startDate',
  CURRENT_INCOMES_EMPLOYER_NAME = 'currentIncomes[].employer.name',
  CURRENT_INCOMES_EMPLOYER_EMAIL = 'currentIncomes[].employer.email',
  CURRENT_INCOMES_EMPLOYER_PHONE = 'currentIncomes[].employer.phone',
  CURRENT_INCOMES_NOTE = 'currentIncomes[].note',
  CURRENT_INCOMES_DOCUMENTS = 'currentIncomes[].documents',
  PAST_JOBS_TITLE = 'pastJobs[].title',
  PAST_JOBS_ORGANIZATION = 'pastJobs[].employer.organization',
  PAST_JOBS_START_DATE = 'pastJobs[].startDate',
  PAST_JOBS_END_DATE = 'pastJobs[].endDate',
  PAST_JOBS_EMPLOYER_NAME = 'pastJobs[].employer.name',
  PAST_JOBS_EMPLOYER_EMAIL = 'pastJobs[].employer.email',
  PAST_JOBS_EMPLOYER_PHONE = 'pastJobs[].employer.phone',
  MOVE_IN_DATE = 'moveInDate',
  MOVE_IN_FLEXIBLE = 'isMoveInDateFlexible',
  PREFERRED_LEASE_LENGTHS = 'preferredLeaseLengths',
  LEASE_LENGTH_FLEXIBLE = 'isLeaseLengthFlexible',
  CURRENT_RESIDENCE_ZPID = 'currentResidence.zpid',
  CURRENT_RESIDENCE_TYPE = 'currentResidence.type',
  CURRENT_RESIDENCE_STREET = 'currentResidence.address.street',
  CURRENT_RESIDENCE_CITY = 'currentResidence.address.city',
  CURRENT_RESIDENCE_STATE = 'currentResidence.address.state',
  CURRENT_RESIDENCE_ZIP = 'currentResidence.address.zip',
  CURRENT_RESIDENCE_START_DATE = 'currentResidence.startDate',
  CURRENT_RESIDENCE_AMOUNT = 'currentResidence.monthlyPayment',
  REASON_FOR_MOVING = 'reasonForMoving',
  CURRENT_RESIDENCE_MANAGER_NAME = 'currentResidence.manager.name',
  CURRENT_RESIDENCE_MANAGER_EMAIL = 'currentResidence.manager.email',
  CURRENT_RESIDENCE_MANAGER_PHONE = 'currentResidence.manager.phone',
  CURRENT_RESIDENCE_DESCRIPTION = 'currentResidence.description',
  PAST_RESIDENCES_TYPE = 'pastResidences[].type',
  PAST_RESIDENCES_STREET = 'pastResidences[].address.street',
  PAST_RESIDENCES_CITY = 'pastResidences[].address.city',
  PAST_RESIDENCES_STATE = 'pastResidences[].address.state',
  PAST_RESIDENCES_ZIP = 'pastResidences[].address.zip',
  PAST_RESIDENCES_START_DATE = 'pastResidences[].startDate',
  PAST_RESIDENCES_END_DATE = 'pastResidences[].endDate',
  PAST_RESIDENCES_MANAGER_NAME = 'pastResidences[].manager.name',
  PAST_RESIDENCES_MANAGER_EMAIL = 'pastResidences[].manager.email',
  PAST_RESIDENCES_MANAGER_PHONE = 'pastResidences[].manager.phone',
  PAST_RESIDENCES_DESCRIPTION = 'pastResidences[].description',
  BACKGROUND_REPORT_COMMENT = 'backgroundReportComment',
  CREDIT_REPORT_COMMENT = 'creditReportComment',
  HAS_VEHICLES = 'vehicles.hasVehicles',
  VEHICLES_MAKE = 'vehicles.vehicles[].make',
  VEHICLES_MODEL = 'vehicles.vehicles[].model',
  VEHICLES_IS_PARKING_SPOT_NEEDED = 'vehicles.vehicles[].isParkingSpotNeeded',
  VEHICLES_IS_EV_CHARGING_NEEDED = 'vehicles.vehicles[].isEvChargingNeeded',
  ADDITIONAL_OCCUPANTS_FULL_NAME = 'additionalOccupants[].fullName',
  ADDITIONAL_OCCUPANTS_DATE_OF_BIRTH = 'additionalOccupants[].dateOfBirth',
  IS_IDENTITY_VERIFIED = 'isIdentityVerified',
  IS_PAYMENT_VALID = 'isPaymentValid',
}

const GUARANTOR_CURRENT_RESIDENCE_FIELDS: Array<WizardFieldType> = [
  { id: FIELD_IDS.CURRENT_RESIDENCE_TYPE },
  { id: FIELD_IDS.CURRENT_RESIDENCE_STREET },
  { id: FIELD_IDS.CURRENT_RESIDENCE_CITY },
  { id: FIELD_IDS.CURRENT_RESIDENCE_STATE },
  { id: FIELD_IDS.CURRENT_RESIDENCE_ZIP },
  { id: FIELD_IDS.CURRENT_RESIDENCE_START_DATE },
  { id: FIELD_IDS.CURRENT_RESIDENCE_AMOUNT },
];

const ALL_SECTIONS: Array<WizardSectionType> = [
  {
    id: SECTION_IDS.PERSONAL_INFO,
    label: 'Personal',
    steps: [
      {
        id: STEP_IDS.LEGAL_NAME,
        label: 'Legal name',
        fields: [
          { id: FIELD_IDS.FIRST_NAME },
          { id: FIELD_IDS.MIDDLE_NAME, optional: true },
          { id: FIELD_IDS.LAST_NAME },
        ],
      },
      {
        id: STEP_IDS.CONTACT,
        label: 'Contact information',
        fields: [{ id: FIELD_IDS.PHONE }],
      },
      {
        id: STEP_IDS.MOVE_IN_DATE,
        label: 'Move-in date',
        fields: [
          {
            id: FIELD_IDS.MOVE_IN_DATE,
            validationRule: getIsMoveInDateValid,
          },
          { id: FIELD_IDS.MOVE_IN_FLEXIBLE },
          {
            id: FIELD_IDS.PREFERRED_LEASE_LENGTHS,
            optional: true,
            validationRule: getIsPreferredLeaseLengthsValid,
          },
          { id: FIELD_IDS.LEASE_LENGTH_FLEXIBLE, optional: true },
        ],
      },
    ],
  },
  {
    id: SECTION_IDS.HOUSEHOLD_INFO,
    label: 'Household',
    steps: [
      {
        id: STEP_IDS.OCCUPANTS,
        label: 'Occupants',
        fields: [
          { id: FIELD_IDS.OCCUPANTS },
          {
            id: FIELD_IDS.MOBILITY_IMPAIRED,
            visibilityRule: {
              dependentLocation: getIsMobilityImpairedVisible,
            },
          },
        ],
      },
      {
        id: STEP_IDS.SMOKING,
        label: 'Smoking',
        fields: [{ id: FIELD_IDS.IS_SMOKER }],
      },
      {
        id: STEP_IDS.PETS,
        label: 'Pets',
        fields: [
          { id: FIELD_IDS.SMALL_DOGS, optional: true },
          { id: FIELD_IDS.LARGE_DOGS, optional: true },
          { id: FIELD_IDS.CATS, optional: true },
          { id: FIELD_IDS.PETS_OTHER, optional: true },
          { id: FIELD_IDS.PETS_DESCRIPTION, optional: true },
        ],
      },
    ],
  },
  {
    id: SECTION_IDS.INCOME_HISTORY,
    label: 'Income',
    steps: [
      {
        id: STEP_IDS.CURRENT_INCOME,
        label: 'Current income',
        fields: [
          { id: FIELD_IDS.CURRENT_INCOMES_TYPE },
          { id: FIELD_IDS.CURRENT_INCOMES_AMOUNT },
          { id: FIELD_IDS.CURRENT_INCOMES_TITLE },
          { id: FIELD_IDS.CURRENT_INCOMES_DOCUMENTS, validationRule: getIsIncomeDocumentsValid, optional: true },
          {
            id: FIELD_IDS.CURRENT_INCOMES_ORGANIZATION,
            visibilityRule: {
              dependentFieldId: FIELD_IDS.CURRENT_INCOMES_TYPE,
              dependentFieldValue: (value) => value === IncomeType.EMPLOYMENT,
            },
          },
          {
            id: FIELD_IDS.CURRENT_INCOMES_START_DATE,
            visibilityRule: {
              dependentFieldId: FIELD_IDS.CURRENT_INCOMES_TYPE,
              dependentFieldValue: (value) => value === IncomeType.EMPLOYMENT,
            },
          },
          {
            id: FIELD_IDS.CURRENT_INCOMES_EMPLOYER_NAME,
            visibilityRule: {
              dependentFieldId: FIELD_IDS.CURRENT_INCOMES_TYPE,
              dependentFieldValue: (value) => value === IncomeType.EMPLOYMENT,
            },
          },
          {
            id: FIELD_IDS.CURRENT_INCOMES_EMPLOYER_EMAIL,
            visibilityRule: {
              dependentFieldId: FIELD_IDS.CURRENT_INCOMES_TYPE,
              dependentFieldValue: (value) => value === IncomeType.EMPLOYMENT,
            },
            optional: true,
          },
          {
            id: FIELD_IDS.CURRENT_INCOMES_EMPLOYER_PHONE,
            visibilityRule: {
              dependentFieldId: FIELD_IDS.CURRENT_INCOMES_TYPE,
              dependentFieldValue: (value) => value === IncomeType.EMPLOYMENT,
            },
            optional: true,
          },
          { id: FIELD_IDS.CURRENT_INCOMES_NOTE, optional: true },
        ],
      },
      {
        id: STEP_IDS.REQUIRED_DOCUMENTS,
        label: 'Income documents',
        fields: [],
        visibilityRule: getIsIncomeDocumentsStepVisible,
      },
      {
        id: STEP_IDS.PAST_JOBS,
        label: 'Past jobs',
        fields: [
          {
            id: FIELD_IDS.PAST_JOBS_TITLE,
            visibilityRule: {
              dependentFieldId: 'pastJobs[]',
              dependentFieldValue: (value) => !!value,
            },
          },
          {
            id: FIELD_IDS.PAST_JOBS_ORGANIZATION,
            visibilityRule: {
              dependentFieldId: 'pastJobs[]',
              dependentFieldValue: (value) => !!value,
            },
          },
          {
            id: FIELD_IDS.PAST_JOBS_START_DATE,
            visibilityRule: {
              dependentFieldId: 'pastJobs[]',
              dependentFieldValue: (value) => !!value,
            },
          },
          {
            id: FIELD_IDS.PAST_JOBS_END_DATE,
            visibilityRule: {
              dependentFieldId: 'pastJobs[]',
              dependentFieldValue: (value) => !!value,
            },
          },
          {
            id: FIELD_IDS.PAST_JOBS_EMPLOYER_NAME,
            visibilityRule: {
              dependentFieldId: 'pastJobs[]',
              dependentFieldValue: (value) => !!value,
            },
          },
          {
            id: FIELD_IDS.PAST_JOBS_EMPLOYER_EMAIL,
            visibilityRule: {
              dependentFieldId: 'pastJobs[]',
              dependentFieldValue: (value) => !!value,
            },
            optional: true,
          },
          {
            id: FIELD_IDS.PAST_JOBS_EMPLOYER_PHONE,
            visibilityRule: {
              dependentFieldId: 'pastJobs[]',
              dependentFieldValue: (value) => !!value,
            },
            optional: true,
          },
        ],
      },
    ],
  },
  {
    id: SECTION_IDS.RESIDENCE_HISTORY,
    label: 'Residence',
    steps: [
      {
        id: STEP_IDS.CURRENT_RESIDENCE,
        label: 'Current residence',
        fields: [
          { id: FIELD_IDS.CURRENT_RESIDENCE_STREET },
          { id: FIELD_IDS.CURRENT_RESIDENCE_CITY },
          { id: FIELD_IDS.CURRENT_RESIDENCE_STATE },
          { id: FIELD_IDS.CURRENT_RESIDENCE_ZIP },
          { id: FIELD_IDS.CURRENT_RESIDENCE_START_DATE },
          { id: FIELD_IDS.CURRENT_RESIDENCE_AMOUNT },
          {
            id: FIELD_IDS.REASON_FOR_MOVING,
            visibilityRule: {
              dependentFieldId: FIELD_IDS.CURRENT_RESIDENCE_TYPE,
              dependentFieldValue: (value) => value !== HomeType.OTHER,
            },
          },
          {
            id: FIELD_IDS.CURRENT_RESIDENCE_MANAGER_NAME,
            visibilityRule: {
              dependentFieldId: FIELD_IDS.CURRENT_RESIDENCE_TYPE,
              dependentFieldValue: (value) => value === HomeType.RENT,
            },
          },
          {
            id: FIELD_IDS.CURRENT_RESIDENCE_MANAGER_EMAIL,
            visibilityRule: {
              dependentFieldId: FIELD_IDS.CURRENT_RESIDENCE_TYPE,
              dependentFieldValue: (value) => value === HomeType.RENT,
            },
            optional: true,
          },
          {
            id: FIELD_IDS.CURRENT_RESIDENCE_MANAGER_PHONE,
            visibilityRule: {
              dependentFieldId: FIELD_IDS.CURRENT_RESIDENCE_TYPE,
              dependentFieldValue: (value) => value === HomeType.RENT,
            },
          },
          {
            id: FIELD_IDS.CURRENT_RESIDENCE_DESCRIPTION,
            visibilityRule: {
              dependentFieldId: FIELD_IDS.CURRENT_RESIDENCE_TYPE,
              dependentFieldValue: (value) => value === HomeType.OTHER,
            },
            optional: true,
          },
        ],
      },
      {
        id: STEP_IDS.PAST_RESIDENCES,
        label: 'Past residences',
        fields: [
          {
            id: FIELD_IDS.PAST_RESIDENCES_STREET,
            visibilityRule: {
              dependentFieldId: 'pastResidences[]',
              dependentFieldValue: (value) => !!value,
            },
          },
          {
            id: FIELD_IDS.PAST_RESIDENCES_CITY,
            visibilityRule: {
              dependentFieldId: 'pastResidences[]',
              dependentFieldValue: (value) => !!value,
            },
          },
          {
            id: FIELD_IDS.PAST_RESIDENCES_STATE,
            visibilityRule: {
              dependentFieldId: 'pastResidences[]',
              dependentFieldValue: (value) => !!value,
            },
          },
          {
            id: FIELD_IDS.PAST_RESIDENCES_ZIP,
            visibilityRule: {
              dependentFieldId: 'pastResidences[]',
              dependentFieldValue: (value) => !!value,
            },
          },
          {
            id: FIELD_IDS.PAST_RESIDENCES_START_DATE,
            visibilityRule: {
              dependentFieldId: 'pastResidences[]',
              dependentFieldValue: (value) => !!value,
            },
          },
          {
            id: FIELD_IDS.PAST_RESIDENCES_END_DATE,
            visibilityRule: {
              dependentFieldId: 'pastResidences[]',
              dependentFieldValue: (value) => !!value,
            },
          },
          {
            id: FIELD_IDS.PAST_RESIDENCES_MANAGER_NAME,
            visibilityRule: {
              dependentFieldId: FIELD_IDS.PAST_RESIDENCES_TYPE,
              dependentFieldValue: (value) => value === HomeType.RENT,
            },
          },
          {
            id: FIELD_IDS.PAST_RESIDENCES_MANAGER_EMAIL,
            visibilityRule: {
              dependentFieldId: FIELD_IDS.PAST_RESIDENCES_TYPE,
              dependentFieldValue: (value) => value === HomeType.RENT,
            },
            optional: true,
          },
          {
            id: FIELD_IDS.PAST_RESIDENCES_MANAGER_PHONE,
            visibilityRule: {
              dependentFieldId: FIELD_IDS.PAST_RESIDENCES_TYPE,
              dependentFieldValue: (value) => value === HomeType.RENT,
            },
          },
          {
            id: FIELD_IDS.PAST_RESIDENCES_DESCRIPTION,
            visibilityRule: {
              dependentFieldId: FIELD_IDS.PAST_RESIDENCES_TYPE,
              dependentFieldValue: (value) => value === HomeType.OTHER,
            },
            optional: true,
          },
        ],
      },
    ],
  },
  {
    id: SECTION_IDS.ID_VERIFICATION,
    label: 'ID verification',
    steps: [
      {
        id: STEP_IDS.ID_VERIFICATION,
        label: 'Identity verification',
        fields: [{ id: FIELD_IDS.IS_IDENTITY_VERIFIED, isUserStatusComplete: getIsIdvStepComplete }],
      },
    ],
  },
  {
    id: SECTION_IDS.PAYMENT_AND_REPORTS,
    label: 'Screening reports',
    steps: [
      {
        id: STEP_IDS.PAYMENT_AND_REPORTS,
        label: 'Screening reports',
        fields: [
          {
            id: FIELD_IDS.IS_PAYMENT_VALID,
            validationRule: getIsPaymentAndReportsStepValid,
            isUserStatusComplete: getIsPaymentStepComplete,
          },
        ],
      },
    ],
  },
  {
    id: SECTION_IDS.SUPPLEMENTAL,
    label: 'Supplemental',
    steps: [
      {
        id: STEP_IDS.SUPPLEMENTAL,
        label: 'Required questions',
        fields: [
          { id: FIELD_IDS.HAS_VEHICLES, optional: true },
          { id: FIELD_IDS.VEHICLES_MAKE, optional: true },
          { id: FIELD_IDS.VEHICLES_MODEL, optional: true },
          { id: FIELD_IDS.VEHICLES_IS_PARKING_SPOT_NEEDED, optional: true },
          { id: FIELD_IDS.VEHICLES_IS_EV_CHARGING_NEEDED, optional: true },
          { id: FIELD_IDS.ADDITIONAL_OCCUPANTS_FULL_NAME, optional: true },
          {
            id: FIELD_IDS.ADDITIONAL_OCCUPANTS_DATE_OF_BIRTH,
            validationRule: getIsAdditionalOccupantDateOfBirthValid,
            optional: true,
          },
        ],
        visibilityRule: getIsSupplementalStepVisible,
      },
    ],
  },
];

const GUARANTOR_HIDDEN_SECTION_IDS: ReadonlySet<SectionId> = new Set([
  SECTION_IDS.HOUSEHOLD_INFO,
  SECTION_IDS.SUPPLEMENTAL,
]);

export function getIsIncomeDocumentsStepVisible(featureFlags: FeatureFlags) {
  return featureFlags.hasIncomeDocumentsStep === true;
}

export function getIsSupplementalStepVisible(featureFlags: FeatureFlags) {
  return featureFlags.hasSupplementalQuestions === true;
}

export function getIsGuarantorWizard(featureFlags: FeatureFlags, role: ApplicationRole | null | undefined) {
  return featureFlags.isGuarantorInvitationsEnabled === true && role === ApplicationRole.GUARANTOR;
}

export function shouldShowApplicantGuarantorForRole(role: ApplicationRole | null | undefined) {
  return role !== ApplicationRole.GUARANTOR;
}

export function getSections(featureFlags: FeatureFlags, role: ApplicationRole | null | undefined) {
  const isGuarantorWizard = getIsGuarantorWizard(featureFlags, role);
  return ALL_SECTIONS.filter((section) => !isGuarantorWizard || !GUARANTOR_HIDDEN_SECTION_IDS.has(section.id))
    .map((section) => ({
      ...section,
      steps: getSectionSteps(section, featureFlags, isGuarantorWizard),
    }))
    .filter((section) => section.steps.length > 0);
}

function getSectionSteps(section: WizardSectionType, featureFlags: FeatureFlags, isGuarantorWizard: boolean) {
  const visibleSteps = section.steps.filter(
    (step) =>
      step.visibilityRule?.(featureFlags) !== false && (!isGuarantorWizard || step.id !== STEP_IDS.MOVE_IN_DATE),
  );

  if (!isGuarantorWizard || section.id !== SECTION_IDS.RESIDENCE_HISTORY) {
    return visibleSteps;
  }

  return visibleSteps
    .filter((step) => step.id === STEP_IDS.CURRENT_RESIDENCE)
    .map((step) => ({ ...step, fields: GUARANTOR_CURRENT_RESIDENCE_FIELDS }));
}

export enum REVIEW_STATUS {
  COMPLETE = 'complete',
  INCOMPLETE = 'incomplete',
  NOT_STARTED = 'not-started',
}

export function getSteps(featureFlags: FeatureFlags, role: ApplicationRole | null | undefined) {
  return getSections(featureFlags, role).reduce(
    (stepsArray, section) => [...stepsArray, ...section.steps],
    [] as Array<WizardStepType>,
  );
}

export function getFields(featureFlags: FeatureFlags, role: ApplicationRole | null | undefined) {
  return getSteps(featureFlags, role).reduce(
    (fieldsArray, step) => [...fieldsArray, ...step.fields],
    [] as Array<WizardFieldType>,
  );
}

export function getNextStep(stepId: string, featureFlags: FeatureFlags, role: ApplicationRole | null | undefined) {
  const steps = getSteps(featureFlags, role);
  const stepIndex = steps.findIndex((step) => step.id === stepId);
  if (stepIndex + 1 >= steps.length) {
    // Last step
    return null;
  }
  return steps[stepIndex + 1];
}

/**
 * The next step a renter can reach from a step that exists but is currently hidden. Returns
 * undefined for an id that names no step, so an unknown stepId keeps its existing behavior
 */
export function getNextVisibleStep(
  stepId: string,
  featureFlags: FeatureFlags,
  role: ApplicationRole | null | undefined,
) {
  const allSteps = ALL_SECTIONS.flatMap((section) => section.steps);
  const startIndex = allSteps.findIndex((step) => step.id === stepId);
  if (startIndex === -1) {
    return undefined;
  }
  const visibleIds = new Set(getSteps(featureFlags, role).map((step) => step.id));
  return allSteps.slice(startIndex + 1).find((step) => visibleIds.has(step.id));
}

export function getPreviousStep(
  stepId: string | undefined,
  featureFlags: FeatureFlags,
  role: ApplicationRole | null | undefined,
) {
  const steps = getSteps(featureFlags, role);
  if (!stepId) {
    // On review page - return last step
    return steps[steps.length - 1];
  }
  const stepIndex = steps.findIndex((step) => step.id === stepId);
  if (stepIndex === 0) {
    // First step
    return null;
  }
  return steps[stepIndex - 1];
}

export function getSectionIndex(
  stepId: string | undefined,
  featureFlags: FeatureFlags,
  role: ApplicationRole | null | undefined,
) {
  const SECTIONS = getSections(featureFlags, role);
  return stepId ? SECTIONS.findIndex((section) => section.steps.some((step) => step.id === stepId)) : SECTIONS.length;
}

export function getIsMobilityImpairedVisible(rentalListing: RentalListing) {
  return (
    rentalListing?.address?.city?.toLowerCase() === 'portland' && rentalListing.address.state?.toLowerCase() === 'or'
  );
}
